import {AsyncDataLoading} from '@frontend/components/AsyncDataLoading'
import {trans} from '@frontend/helpers'
import type {
    AdPreviewDataForGrid,
    AdPreviewDataForList,
    AdsListGridColumnsConfig,
    AdsListPresentation,
    FilteredItemsPageUrlQueryParams,
} from '@frontend/modules/public/ads/AdsModuleTypes'
import {useFilteredItemsLoader} from '@frontend/modules/public/ads/hooks/useFilteredItemsLoader'
import {
    useCallback,
    useEffect,
} from 'react'
import {Badge} from 'swayok-react-mdb-ui-kit/components/Typography/Badge'
import type {UILayout} from 'swayok-react-mdb-ui-kit/types/Common'
import {AdsList} from './AdsList'

interface AdsFromOtherLocationsProps {
    categoryId: number | null
    filters: Partial<FilteredItemsPageUrlQueryParams>
    adsPresentation: AdsListPresentation
    // Количество объявлений на странице.
    // 20 и 40 делится на 4 и на 5, что удобно для таблиц с 2, 4 или 5 колонками.
    pageLimit?: number
    gridColumns: AdsListGridColumnsConfig | number
    sideMenuVisibleFromBsBreakpoint: UILayout['bootstrapSize']
}

// Список объявлений для страницы поиска объявлений.
export function AdsFromOtherLocations(props: AdsFromOtherLocationsProps) {

    const {
        categoryId,
        filters,
        adsPresentation = 'grid',
        pageLimit = adsPresentation === 'list' ? 10 : 20,
        gridColumns,
        sideMenuVisibleFromBsBreakpoint,
    } = props

    const {
        loadItems,
        isLoading,
        items,
        totalItemsCount,
        loadingError,
        isAllItemsLoaded,
        onItemDataChange,
    } = useFilteredItemsLoader<AdPreviewDataForGrid | AdPreviewDataForList>({
        type: 'ads',
        listIdForAnalytics: 'from_other_locations',
        categoryId,
        adsPresentation,
        filters: {
            ...filters,
            location: null,
            exceptLocation: filters.exceptLocation ?? filters.location ?? null,
        },
        pageLimit,
    })

    const onLoadMore = useCallback(
        (infiniteScroll?: boolean) => loadItems('next', false, infiniteScroll),
        [loadItems]
    )

    // Загрузка объявлений при изменении фильтров (включая монтирование).
    useEffect(() => {
        loadItems('filter')
    }, [categoryId, JSON.stringify({...filters, location: null})])

    // Если нет объявлений в других локациях - не показываем этот блок вообще.
    if (totalItemsCount === 0) {
        return null
    }

    return (
        <AsyncDataLoading
            loading={isLoading !== null && isLoading !== 'next'}
            className="flex-1"
            error={loadingError}
            onReload={() => loadItems('filter')}
            render={() => (
                <div id="offered-items-from-other-locations">
                    <h2 className="my-4 d-flex flex-row justify-content-start align-items-center">
                        {trans().ads.list.ads_from_other_locations}
                        <Badge
                            pill
                            color="red"
                            className="ms-3 fs-6 fw-400 align-middle d-inline-block"
                        >
                            {totalItemsCount}
                        </Badge>
                    </h2>
                    <AdsList
                        listUid="search_results_from_other_locations"
                        ads={items}
                        isAllAdsLoaded={isAllItemsLoaded}
                        isLoadingMoreItems={isLoading === 'next'}
                        onLoadMore={onLoadMore}
                        presentation={adsPresentation}
                        gridColumns={gridColumns}
                        sideMenuVisibleFromBsBreakpoint={sideMenuVisibleFromBsBreakpoint}
                        allowPromoBanners
                        disabled={isLoading == 'next'}
                        onAdDataChange={onItemDataChange}
                        infiniteScroll
                    />
                </div>
            )}
        />
    )
}
